import { apiClient } from './config.js';

export type ProviderState = 'healthy' | 'degraded' | 'down';

export interface ProviderHealth {
  provider_name: string;
  state: ProviderState;
  last_success_at: string | null;
  last_failure_at: string | null;
  consecutive_failures: number;
  consecutive_successes: number;
  last_error_class: string | null;
  last_error_message: string | null;
  state_changed_at: string;
  next_health_probe_at: string | null;
}

export interface ProviderFailure {
  failure_id: string;
  provider_name: string;
  phase: string;
  call_site: string;
  model_family: string | null;
  input_kind: string;
  input_id: string | null;
  job_id: string | null;
  error_class: string;
  error_message: string;
  attempted_at: string;
  recovery_status: string;
  replayed_at: string | null;
}

export interface BacklogJob {
  job_id: string;
  job_type: string;
  held_for_provider: string;
  priority: number;
  skip_until: string | null;
  held_failure_id: string | null;
  created_at: string;
  payload: Record<string, unknown> | null;
  error_class: string | null;
  error_message: string | null;
  held_since: string | null;
}

export async function getProviderHealth(): Promise<{ providers: ProviderHealth[] }> {
  const response = await apiClient.get('/admin/provider-health');
  return response.data;
}

export async function getProviderFailures(
  providerName: string,
  limit = 50,
): Promise<{ failures: ProviderFailure[]; count: number }> {
  const response = await apiClient.get(
    `/admin/provider-health/${providerName}/failures?limit=${limit}`,
  );
  return response.data;
}

export async function getBacklog(): Promise<{
  groups: Record<string, BacklogJob[]>;
  total: number;
}> {
  const response = await apiClient.get('/admin/backlog');
  return response.data;
}

export async function updateBacklogJob(
  jobId: string,
  patch: { priority?: number; skip_until?: string | null },
): Promise<{ job_id: string; updated: boolean }> {
  const response = await apiClient.patch(`/admin/backlog/${jobId}`, patch);
  return response.data;
}

export async function deleteBacklogJob(
  jobId: string,
): Promise<{ job_id: string; deleted: boolean }> {
  const response = await apiClient.delete(`/admin/backlog/${jobId}`);
  return response.data;
}

export async function replayBacklogJob(
  jobId: string,
): Promise<{ job_id: string; replayed: boolean }> {
  const response = await apiClient.post(`/admin/backlog/${jobId}/replay`);
  return response.data;
}
